import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { getCurrentStep } from '../store/flow/selectors';
import { devices } from '../styles/viewports.styles';
import { GreetingsPretitle } from '../views-components/GreetingsPretitle';
import { StepSubtitle } from '../views-components/StepSubtitle';
import { StepTitle } from '../views-components/StepTitle';
import { Step } from './Step';
import { LayoutTemplateBelowTitleContainer, LayoutTemplateContentContainer } from '../app/AppViewComponent.styles';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useIndustry } from '../step-industry/useIndustry';
import { hasManagementLevelEnabled } from '../managementLevel/managementLevelUtils';
const BaseViewHeader = styled.header.withConfig({
  displayName: "BaseView__BaseViewHeader",
  componentId: "sc-3btd5l-0"
})(["margin-bottom:1.5rem;", " @media ", "{", "}@media ", "{", " ", " ", "}", ""], props => (props.isFramedCentredStep || props.shouldUseLayoutTemplate) && `text-align: center;`, devices.tablet, props => props.isACQ134Variant && `max-width: 816px; margin: auto;`, devices.desktop, props => props.isFramedCentredStep && !props.shouldUseLayoutTemplate && `padding-top: 30px;`, props => props.isFramedCentredStep && props.stepKey === Step.HubletSelection && `margin-bottom: 0;`, props => props.isFramedCentredStep && props.stepKey === Step.JobRole && `margin-left: 20px;`, props => props.stepKey === Step.JobRole && !props.shouldUseLayoutTemplate && `
  @media ${devices.desktop} {
    width: 90%;
  }
  `);
const BaseViewHelperText = styled.div.withConfig({
  displayName: "BaseView__BaseViewHelperText",
  componentId: "sc-3btd5l-1"
})(["", " ", " ", " ", ""], props => props.isFramedCentredStep && ![Step.CrmExperienceLevel].includes(props.stepKey) && `text-align: left;`, props => props.shouldUseLayoutTemplate && `text-align: center;`, props => props.stepKey === Step.HubletSelection && props.shouldUseLayoutTemplate && `text-align: left !important;`, props => props.isACQ134Variant && `margin-bottom: 16px;`);
export const BaseView = ({
  title,
  helperText,
  footer,
  children,
  stepKey,
  navMarker,
  preTitleMessage = '',
  addExperimentClassName = false,
  disableAnimation = false,
  expandableSubTitleSection = null
}) => {
  const currentStep = useSelector(getCurrentStep);
  const step = stepKey || currentStep;
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  const {
    shouldUseLayoutTemplate,
    layoutType
  } = useLayoutTemplate();
  const {
    isAssignedToVariant: isAssignedToVariantACQ0134,
    isExperimentActive
  } = useExperiment(ExperimentKeys.ACQ0134, TrackWhen.never);
  const {
    clickedToSeeAll,
    predictedIndustries
  } = useIndustry();
  const predictions = predictedIndustries.length > 0;

  if (!step) {
    return null;
  }

  let shouldRestrictWidth = step && hasManagementLevelEnabled() ? ![Step.CompanySize, Step.Industry, Step.ManagementLevel].includes(step) : step !== Step.CompanySize;

  if (isAssignedToVariantACQ0134()) {
    shouldRestrictWidth = ![Step.Industry, Step.ManagementLevel, Step.CompanySize, Step.BusinessGoals, Step.HubOption, Step.AhaMoment].includes(step);
  }

  const stepTitle = /*#__PURE__*/_jsx(StepTitle, {
    isFramedCentredStep: isFramedCentredStep,
    stepKey: step
  });

  const navMarkerName = navMarker || `${step}_VIEW`;
  const experimentSeleniumAttribute = addExperimentClassName ? {
    'data-selenium-experiment': 'EXPERIMENT'
  } : null;
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    dataStep: step,
    disableAnimation: disableAnimation,
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: navMarkerName
    }), /*#__PURE__*/_jsxs("div", Object.assign({
      "data-selenium": `${step}_VIEW`
    }, experimentSeleniumAttribute, {
      children: [/*#__PURE__*/_jsxs(BaseViewHeader // @ts-expect-error Expression produces a union type that is too complex to represent.
      , {
        isACQ134Variant: isAssignedToVariantACQ0134(),
        isFramedCentredStep: isFramedCentredStep,
        shouldUseLayoutTemplate: shouldUseLayoutTemplate,
        stepKey: step,
        children: [/*#__PURE__*/_jsx(GreetingsPretitle, {
          hasPreTitle: isValidI18nKey(preTitleMessage)
        }), /*#__PURE__*/_jsx(StepSubtitle, {
          message: preTitleMessage
        }), title || stepTitle, /*#__PURE__*/_jsxs(LayoutTemplateBelowTitleContainer, {
          shouldRestrictWidth: shouldRestrictWidth,
          shouldUseLayoutTemplate: shouldUseLayoutTemplate,
          children: [expandableSubTitleSection, /*#__PURE__*/_jsx(BaseViewHelperText, {
            isACQ134Variant: isAssignedToVariantACQ0134(),
            isFramedCentredStep: isFramedCentredStep,
            shouldUseLayoutTemplate: shouldUseLayoutTemplate,
            stepKey: step,
            children: helperText
          })]
        })]
      }), /*#__PURE__*/_jsxs(LayoutTemplateContentContainer, {
        clickedToSeeAll: isExperimentActive ? clickedToSeeAll : clickedToSeeAll || predictions,
        layoutType: layoutType,
        shouldRestrictWidth: shouldRestrictWidth,
        shouldUseLayoutTemplate: shouldUseLayoutTemplate,
        shouldWidenIndustryStep: isAssignedToVariantACQ0134() || hasManagementLevelEnabled(),
        stepKey: step,
        children: [children, footer]
      })]
    }))]
  });
};